import type { CustomSEO } from '@iheartjane/data-access';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import type { Path, PathValue } from 'react-hook-form';

type FieldName = string;

interface SeoFormContextType {
  insertVariable: (variable: string) => void;
  setupEventListeners: (cursorTrackingFields: FieldName[]) => () => void;
}

const SeoFormContext = createContext<SeoFormContextType | null>(null);

export const SeoFormProvider = ({ children }: { children: ReactNode }) => {
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [activeField, setActiveField] = useState<FieldName | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const { setValue } = useFormContext<CustomSEO>();

  const getInputElement = useCallback(
    (fieldName: FieldName): HTMLInputElement | HTMLTextAreaElement | null => {
      if (!formRef.current) return null;

      const inputElement = formRef.current.querySelector(
        `input[name="${fieldName}"]`
      ) as HTMLInputElement;
      const textareaElement = formRef.current.querySelector(
        `textarea[name="${fieldName}"]`
      ) as HTMLTextAreaElement;

      return inputElement || textareaElement || null;
    },
    []
  );

  const handleInputEvent = useCallback(
    (fieldName: FieldName, element: HTMLInputElement | HTMLTextAreaElement) => {
      const position = element.selectionStart ?? 0;
      setCursorPosition(position);
      setActiveField(fieldName);
    },
    []
  );

  const attachEventListeners = useCallback(
    (cursorTrackingFields: FieldName[]) => {
      const cleanupFunctions: (() => void)[] = [];

      cursorTrackingFields.forEach((fieldName) => {
        const element = getInputElement(fieldName);

        if (element) {
          const handler = () => handleInputEvent(fieldName, element);
          ['focus', 'click', 'keyup'].forEach((eventType) => {
            element.addEventListener(eventType, handler);
          });

          cleanupFunctions.push(() => {
            ['focus', 'click', 'keyup'].forEach((eventType) => {
              element.removeEventListener(eventType, handler);
            });
          });
        }
      });

      return cleanupFunctions;
    },
    [getInputElement, handleInputEvent]
  );

  const setupEventListeners = useCallback(
    (cursorTrackingFields: FieldName[]) => {
      // Try to attach listeners immediately
      let cleanupFunctions = attachEventListeners(cursorTrackingFields);

      // If elements weren't found, retry after a small delay
      if (cleanupFunctions.length === 0) {
        const timeoutId = setTimeout(() => {
          cleanupFunctions = attachEventListeners(cursorTrackingFields);
        }, 100);

        return () => {
          clearTimeout(timeoutId);
          cleanupFunctions.forEach((cleanup) => cleanup());
        };
      }

      return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
      };
    },
    [attachEventListeners]
  );

  const updateField = <TField extends Path<CustomSEO>>(
    field: TField,
    value: PathValue<CustomSEO, TField>
  ) => {
    setValue(field, value);
  };

  const insertVariable = useCallback(
    (variable: string) => {
      if (!activeField) return;

      const element = getInputElement(activeField);
      if (!element) return;

      const currentValue = element.value;
      const position = cursorPosition;
      const variableText = `{{ ${variable} }}`;

      const newValue =
        currentValue.slice(0, position) +
        variableText +
        currentValue.slice(position);

      const newCursorPosition = position + variableText.length;
      const isInsertingAtEnd = position === currentValue.length;

      // Update both the DOM element and the form state
      element.value = newValue;
      element.dispatchEvent(new Event('input', { bubbles: true }));

      updateField(
        activeField as Path<CustomSEO>,
        newValue as PathValue<CustomSEO, Path<CustomSEO>>
      );

      // If inserting at the end, cursor will end up in the correct position
      if (isInsertingAtEnd) {
        element.focus();
        setCursorPosition(newCursorPosition);
        return;
      }

      // For mid-text insertions, use retry logic to ensure cursor position
      const setCursorWithRetry = (attempt = 0) => {
        if (!element || attempt > 5) return;

        element.focus();
        element.setSelectionRange(newCursorPosition, newCursorPosition);
        setCursorPosition(newCursorPosition);

        setTimeout(
          () => {
            if (element.selectionStart !== newCursorPosition) {
              setCursorWithRetry(attempt + 1);
            }
          },
          10 + attempt * 10
        );
      };

      setTimeout(() => setCursorWithRetry(), 0);
    },
    [activeField, cursorPosition, getInputElement]
  );

  return (
    <SeoFormContext.Provider
      value={{
        insertVariable,
        setupEventListeners,
      }}
    >
      <div ref={formRef}>{children}</div>
    </SeoFormContext.Provider>
  );
};

export const useSeoForm = () => {
  const context = useContext(SeoFormContext);
  if (!context) {
    throw new Error('useSeoForm must be used within a SeoFormProvider');
  }
  return context;
};
